import { useMemo } from "react";
import { Chart } from "react-chartjs-2";
import type { ChartData, ChartOptions } from "chart.js";
import { useChartTheme } from "@/lib/chartTheme";
import { registerCharts } from "./register";
import { normalizeColor, withAlpha } from "./utils";

registerCharts();

export interface SankeyFlow {
  from: string;
  to: string;
  flow: number;
}

export type SankeySelection =
  | { kind: "flow"; from: string; to: string }
  | { kind: "node"; node: string };

interface SankeyChartProps {
  flows: SankeyFlow[];
  /** Optional explicit colour per node label. Falls back to the palette. */
  nodeColors?: Record<string, string>;
  height?: number;
  className?: string;
  /** Delay (ms) before the entry animation starts. */
  delay?: number;
  /**
   * Fires when the user clicks a flow ribbon. Node clicks aren't natively
   * separable from flow clicks in chartjs-chart-sankey, so callers that
   * want node-level inspection should pair this with their own UI for
   * selecting nodes (e.g. a stage filter or a separate node legend).
   */
  onSelect?: (sel: SankeySelection) => void;
}

export function SankeyChart({
  flows,
  nodeColors,
  height = 320,
  className,
  delay = 0,
  onSelect,
}: SankeyChartProps) {
  const { tokens, palette } = useChartTheme();

  const data = useMemo<ChartData<"sankey">>(() => {
    // Build a stable colour map for nodes — focal first, palette afterwards.
    // EVERY value in `map` is normalized to a sRGB rgba(…) string so that
    // chartjs-chart-sankey's applyAlpha (which uses @kurkle/color, no oklch
    // support) can parse without crashing.
    const seen = new Set<string>();
    flows.forEach((f) => {
      seen.add(f.from);
      seen.add(f.to);
    });
    const safeFocal = withAlpha(tokens.chartFocal || tokens.accent || "#155ECF", 1);
    const map: Record<string, string> = {};
    let idx = 0;
    for (const node of seen) {
      const raw = nodeColors?.[node] ?? palette[idx % palette.length] ?? safeFocal;
      map[node] = withAlpha(normalizeColor(raw), 1);
      idx++;
    }

    return {
      datasets: [
        {
          label: "Flow",
          data: flows.map((f) => ({ from: f.from, to: f.to, flow: f.flow })),
          // Sankey plugin callbacks aren't typed in core Chart.js
          colorFrom: (ctx: any) =>
            map[(ctx.dataset.data[ctx.dataIndex] as SankeyFlow).from] ?? safeFocal,
          colorTo: (ctx: any) =>
            map[(ctx.dataset.data[ctx.dataIndex] as SankeyFlow).to] ?? safeFocal,
          colorMode: "gradient",
          borderColor: "rgba(0, 0, 0, 0)",
          borderWidth: 0,
          alpha: 0.55,
          nodeWidth: 12,
          nodePadding: 18,
          font: { family: tokens.fontSans, size: 11, weight: 500, color: tokens.fg },
          // Tint each node bar with its mapped colour (already normalized).
          color: (ctx: any) => withAlpha(map[(ctx.raw as SankeyFlow)?.from] ?? safeFocal, 0.9),
        },
      ],
    } as unknown as ChartData<"sankey">;
  }, [flows, nodeColors, tokens, palette]);

  const options = useMemo<ChartOptions<"sankey">>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: tokens.fg,
        titleColor: tokens.surface,
        bodyColor: tokens.surface,
        padding: 10,
        cornerRadius: 6,
        titleFont: { family: tokens.fontSans, weight: 600, size: 12 },
        bodyFont:  { family: tokens.fontMono, weight: 500, size: 12 },
        callbacks: {
          title: () => "",
          label: (ctx: any) => {
            const d = ctx.raw as SankeyFlow;
            return `${d.from} → ${d.to}: ${d.flow.toLocaleString()}`;
          },
        },
      },
    },
    animation: { duration: 800, easing: "easeOutQuart", delay },
    onClick: onSelect
      ? (_e: any, els: any[], chart: any) => {
          if (!els || els.length === 0) return;
          const el = els[0];
          const ds = chart.data.datasets[el.datasetIndex] as any;
          const point = ds?.data?.[el.index];
          if (point && typeof point.from === "string" && typeof point.to === "string") {
            onSelect({ kind: "flow", from: point.from, to: point.to });
          }
        }
      : undefined,
  // Sankey types from the plugin aren't merged into Chart.js' core type registry
  // here; cast lets us use it without piping the augmentation.
  } as unknown as ChartOptions<"sankey">), [tokens, delay, onSelect]);

  return (
    <div className={className} style={{ height }}>
      <Chart type="sankey" data={data} options={options} />
    </div>
  );
}
